import { FC, useEffect, useMemo, useState } from 'react';

import { Globe } from 'lucide-react';
import { isEmpty, isNonNullish } from 'remeda';
import useSWR from 'swr';

import Pagination from '@mui/material/Pagination';

import css from './SameIpUsers.module.css';

import { userProfileService } from 'v2/api/userProfileService';
import { EmptyPlaceholder } from 'v2/components/emptyPlaceholder/EmptyPlaceholder';
import { ProfileCard } from 'v2/components/profileCard/ProfileCard';
import { Select } from 'v2/components/select/Select';
import { UserLink } from 'v2/components/userLink/UserLink';
import { usePagination } from 'v2/hooks/usePagination';
import { SelectOption } from 'v2/types';
import { getPaginationCount } from 'v2/utils';

interface SameIpUsersProps {
  ip: string;
  userId: string;
}

export const SameIpUsers: FC<SameIpUsersProps> = ({ ip, userId }) => {
  const { page, pageSize, onPageChange } = usePagination({ defaultPageSize: 10 });
  const [selectedIp, setSelectedIp] = useState('');

  const ips = useMemo(
    () =>
      ip
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s !== ''),
    [ip]
  );

  useEffect(() => {
    if (ips.length > 0 && !ips.includes(selectedIp)) {
      setSelectedIp(ips[0]);
      onPageChange(1);
      return;
    }

    if (ips.length === 0 && selectedIp !== '') {
      setSelectedIp('');
      onPageChange(1);
    }
  }, [ips, selectedIp, onPageChange]);

  const hasValidIp = selectedIp !== '';

  const ipOptions: SelectOption<string>[] = ips.map((ipAddr) => ({
    id: ipAddr,
    label: ipAddr,
    value: ipAddr,
  }));

  const handleIpChange = (value: string) => {
    setSelectedIp(value);
    onPageChange(1);
  };

  const { data } = useSWR(
    hasValidIp ? ['getUsersByIp', page, pageSize, { ip: selectedIp, excludeUserId: userId }] : null,
    userProfileService.getUsersByIp,
    { keepPreviousData: true }
  );

  const title = ips.length === 1 ? `Players with same IP (${ips[0]})` : 'Players with same IP';

  return (
    <ProfileCard title={title} icon={<Globe width={18} height={18} />}>
      {!hasValidIp && <EmptyPlaceholder />}

      {hasValidIp && isNonNullish(data) && (
        <>
          {ips.length > 1 && (
            <Select label="IP" value={selectedIp} options={ipOptions} onChange={handleIpChange} size="small" />
          )}

          {isEmpty(data.result) ? (
            <EmptyPlaceholder />
          ) : (
            <>
              {data.result.map((user) => (
                <UserLink key={user.id} userId={user.id} nickname={user.userNickname} imgUrl={user.imgUrl} />
              ))}

              {data.total > pageSize && (
                <Pagination
                  className={css.pagination}
                  page={page}
                  count={getPaginationCount(pageSize, data.total)}
                  onChange={(_, p) => onPageChange(p)}
                />
              )}
            </>
          )}
        </>
      )}
    </ProfileCard>
  );
};
